import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { useToaster } from '../components/common/Toaster';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardIconHeader,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { FolderKanban, Plus, Users, ListTodo } from 'lucide-react';
import { taskService } from '../Services/taskService';
import CreateProjectDialog from '../components/Tasks/CreateProjectDialog';
import type { ProjectDto, CreateProjectDto } from '../types/tasks';

const Projects: React.FC = () => {
  const navigate = useNavigate();
  const { notify } = useToaster();
  const [projects, setProjects] = useState<ProjectDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  useEffect(() => {
    const controller = new AbortController();

    const loadProjects = async () => {
      try {
        const data = await taskService.getMyProjects();
        if (!controller.signal.aborted) {
          setProjects(data);
        }
      } catch (error) {
        if (controller.signal.aborted) return;
        console.error('Error fetching projects:', error);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    loadProjects();
    return () => controller.abort();
  }, []);

  const handleCreateProject = async (dto: CreateProjectDto) => {
    const created = await taskService.createProject(dto);
    setProjects((prev) => [...prev, created]);
    notify('Created', 'Project created successfully', 'success');
  };

  if (loading) return <LoadingSpinner label="Loading projects..." />;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <Card>
        <CardIconHeader icon={<FolderKanban size={22} className="text-primary" />}>
          <div className="flex items-center justify-between w-full">
            <div>
              <CardTitle>Projects</CardTitle>
              <CardDescription>
                Manage your team projects and track progress across all boards.
              </CardDescription>
            </div>
            <Button onClick={() => setCreateDialogOpen(true)} className="ml-4">
              <Plus className="mr-2 h-4 w-4" />
              New Project
            </Button>
          </div>
        </CardIconHeader>
      </Card>

      {/* Projects Grid */}
      {projects.length === 0 ? (
        <Card>
          <CardContent>
            <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg py-12">
              <div className="flex flex-col items-center gap-2 text-center">
                <div className="rounded-full bg-muted p-3">
                  <FolderKanban size={24} className="text-muted-foreground" />
                </div>
                <p className="font-medium text-muted-foreground">No projects yet</p>
                <p className="text-sm text-muted-foreground/75">
                  Create your first project to start organizing tasks.
                </p>
                <Button className="mt-2" onClick={() => setCreateDialogOpen(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  Create Project
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 stagger-children">
          {projects.map((project) => (
            <Card
              key={project.id}
              className="group relative overflow-hidden cursor-pointer animate-fade-in-up transition-all duration-200 hover:-translate-y-1 hover:shadow-lg"
              onClick={() => navigate(`/projects/${project.id}`)}
            >
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-primary/80 to-primary/20" />
              <CardHeader>
                <CardTitle className="text-lg line-clamp-1">{project.name}</CardTitle>
                <CardDescription className="line-clamp-2">
                  {project.description || 'No description'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {/* Progress bar */}
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>Progress</span>
                      <span>{project.taskCount > 0 ? 'Active' : 'No tasks'}</span>
                    </div>
                    <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                      <div
                        className="h-full rounded-full bg-primary transition-all"
                        style={{ width: project.taskCount > 0 ? '0%' : '0%' }}
                      />
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1.5">
                      <Users className="h-3.5 w-3.5" />
                      <span>{project.memberCount} member{project.memberCount !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <ListTodo className="h-3.5 w-3.5" />
                      <span>{project.taskCount} task{project.taskCount !== 1 ? 's' : ''}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <CreateProjectDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSubmit={handleCreateProject}
      />
    </div>
  );
};

export default Projects;
